import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { baseurladdress } from "../baseurl";

// ---------------------------------------------------------------------------
// Auth + routes confirmed from your doctor.js:
//   const token = localStorage.getItem("token");
//   axios.get(baseurladdress + "staff/findallstaff", { headers: { Authorization: `Bearer ${token}` }})
//   api.get("doctors/findalldoctors")  (via axios.create({ baseURL: baseurladdress, headers... }))
//
// So staff routes live under "staff/" and doctor routes under "doctors/".
// This component uses the same axios.create(...) pattern as doctor.js.
//
// Behavior preserved from the previous version (per your confirmation):
//   - A staff dropdown still drives which doctors appear in the pool.
//   - The pool only shows doctors whose doctor_servedby matches the
//     selected staff (single-staff view, not an all-staff table).
//   - Dropping a doctor onto a day cell is restricted to the selected
//     staff's own doctors (enforced naturally since the pool itself is
//     already scoped to doctor_servedby === selectedStaffId).
//
// Two things still not covered by your backend (flagged so you don't lose
// track of them):
//
// 1. Core / Secondary category — this now reads the real `doctor_type`
//    column on doctors_table (0 = Core, 1 = Secondary), the same field
//    doctor.js writes when a doctor is added under either tab. Since
//    /doctors/findalldoctors filters by a single doctor_type per call, this
//    file fetches both types in parallel and merges them so both show up in
//    the pool (previously it only ever fetched Core doctors here).
//
// 2. Schedule persistence — now fully backed by appointments_tbl:
//    - Saved appointments (for both the Assign tab and the Staff overview
//      tab) are read live from GET appointment/getweekoverview. Nothing
//      about appointment data touches localStorage.
//    - `schedule` (React state) holds ONLY in-progress drafts — a doctor
//      dropped onto a day but not yet given a time / not yet POSTed via
//      addappointment(bulk). It resets on page reload by design; once a
//      draft is saved it becomes a normal DB-backed row and is read back
//      through overviewData like everything else.
// ---------------------------------------------------------------------------

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_OFFSETS = [0, 1, 2, 3, 4, 5, 6];

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}
// Builds a plain "YYYY-MM-DD" string from a Date's own LOCAL date parts.
// Deliberately NOT using d.toISOString().slice(0,10) — toISOString()
// converts to UTC first, and for any timezone ahead of UTC (e.g. IST,
// +5:30) that silently shifts local midnight back onto the previous
// calendar day. That bug would send appointment_date one day earlier than
// what the calendar column actually shows, and — since it's used
// consistently for both saving AND same-session display — it stayed
// invisible until the page was refreshed and the real stored date was
// read back correctly, at which point the two no longer matched.
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfLocalDay(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}
function addLocalDays(d, days) {
  const date = new Date(d);
  date.setDate(date.getDate() + days);
  return date;
}
// Backend dates can come back as a "YYYY-MM-DD" string, a full ISO
// timestamp string, OR (most commonly with mysql2) a real JS Date object,
// since DATE/DATETIME columns are converted to Date objects unless the
// connection is configured with dateStrings: true.
//
// This matters a lot here: String(someDateObject) does NOT produce
// "2026-07-29" — it produces something like
// "Wed Jul 29 2026 00:00:00 GMT+0530 (India Standard Time)", so
// String(v).slice(0, 10) silently returns "Wed Jul 29" and NEVER matches
// the "YYYY-MM-DD" strings isoDate() produces for the calendar grid. That
// mismatch is why saved appointments weren't showing up under the right day.
function normalizeDateStr(v) {
  if (!v) return "";
  if (v instanceof Date) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  // String value: could be "2026-07-29" or "2026-07-29T00:00:00.000Z" etc.
  return String(v).slice(0, 10);
}
function fmtWeekLabel(d) {
  const end = new Date(d);
  end.setDate(d.getDate() + 5);
  const opts = { month: "short", day: "numeric" };
  return `${d.toLocaleDateString(undefined, opts)} \u2013 ${end.toLocaleDateString(undefined, opts)}, ${d.getFullYear()}`;
}
function fmtTime(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

function appointmentStatusClass(status) {
  if (String(status) === "1") return "status-complete";
  if (String(status) === "0") return "status-incomplete";
  return "";
}

// appoint_clienttype for appointments_tbl doesn't have a doctor-specific
// value defined in the backend yet — this is a placeholder. Change this to
// whatever code your appointments_tbl actually uses to mean "doctor".
const DOCTOR_CLIENT_TYPE = 0;

// Pulls the logged-in user's id (and a display name if present) out of the
// JWT stored in localStorage, since /addappointment(bulk) needs a
// "created by" / "assigned by" user id and the token is all we have here.
function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function DoctorAssignment() {
  const token = localStorage.getItem("token");

  const api = axios.create({
    baseURL: baseurladdress,
    headers: { Authorization: `Bearer ${token}` },
  });

  // Assumption: the JWT payload carries the logged-in user's id under
  // user_id or id, and (optionally) a display name. Adjust the field names
  // below if your token shape differs.
  const decodedUser = useMemo(() => decodeJwtPayload(token), [token]);
  const loggedInUserId = decodedUser?.user_id || decodedUser?.id || null;
  const loggedInUserName =
    decodedUser?.user_name || decodedUser?.name || decodedUser?.username || "Admin";

  const [staffList, setStaffList] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [loadingDoctors, setLoadingDoctors] = useState(true);

  const [search, setSearch] = useState("");

  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  // schedule holds ONLY unsaved drafts, in memory for this session:
  // { [weekIso]: { [staffId]: { [day]: [{id,timeFrom,timeTo}] } } }
  // Anything already in appointments_tbl is read live from overviewData
  // (see savedListFor/listFor) — nothing appointment-related touches
  // localStorage anymore.
  const [schedule, setSchedule] = useState({});
  const [hiddenAppointIds, setHiddenAppointIds] = useState(new Set());
  const [cap, setCap] = useState(5);

  const [dragDocId, setDragDocId] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [timeModal, setTimeModal] = useState(null); // { day, item, doc }
  const [timeSaving, setTimeSaving] = useState(false);
  const [saving, setSaving] = useState(false);

  const [viewMode, setViewMode] = useState("assign"); // "assign" | "overview"
  const [overviewData, setOverviewData] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [duplicatingSchedule, setDuplicatingSchedule] = useState(false);

  // Assign tab's calendar reads from THIS — appointments for just the
  // currently selected staff, straight from GET appointment/getbystaff/:staffId.
  // (overviewData above is the separate "all staff" feed used by the
  // Staff overview tab and for cross-staff double-booking checks.)
  const [staffAppointments, setStaffAppointments] = useState([]);
  const [, setStaffAppointmentsLoading] = useState(false);

  // Auto-assign mode state
  const [selectedDoctorIds, setSelectedDoctorIds] = useState(new Set());
  const [autoAssignModal, setAutoAssignModal] = useState(null); // { coreCount, secondaryCount, totalCount }
  const [autoAssigning, setAutoAssigning] = useState(false);

  const weekKey = isoDate(weekStart);

  // ---- fetch staff ----
  useEffect(() => {
    const getAllStaff = async () => {
      setLoadingStaff(true);
      try {
        const res = await axios.get(baseurladdress + "staff/findallstaff", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data.status === "Success" ? res.data.staff || [] : [];
        setStaffList(list);
      } catch (err) {
        console.error("findallstaff error:", err);
        Swal.fire("Error", "Couldn't load staff list.", "error");
        setStaffList([]);
      } finally {
        setLoadingStaff(false);
      }
    };
    getAllStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---- fetch doctors for the selected staff ----
  useEffect(() => {
    const getDoctorsByStaff = async () => {
      if (!selectedStaffId) {
        setDoctors([]);
        setLoadingDoctors(false);
        return;
      }

      setLoadingDoctors(true);
      try {
        const res = await api.get(`doctors/doctorsbystaff/${selectedStaffId}`);
        const list = res.data.status === "Success" ? res.data.doctors || [] : [];
        setDoctors(list);
      } catch (err) {
        console.error("doctorsbystaff error:", err);
        Swal.fire("Error", "Couldn't load doctor list for this staff.", "error");
        setDoctors([]);
      } finally {
        setLoadingDoctors(false);
      }
    };
    getDoctorsByStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId, token]);

  const selectedStaff = useMemo(
    () => staffList.find((s) => String(s.staff_id) === String(selectedStaffId)),
    [staffList, selectedStaffId]
  );

  // doctors served by the selected staff — this is the staff-scoped list from
  // GET /doctorsbystaff/:staff_id. It contains each doctor's allowed days.
  const doctorsForStaff = useMemo(() => {
    if (!selectedStaffId) return [];
    return doctors.filter(
      (d) => !d.doctor_servedby || String(d.doctor_servedby) === String(selectedStaffId)
    );
  }, [doctors, selectedStaffId]);

  const doctorsFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return doctorsForStaff.filter((d) => {
      const matchesSearch =
        !q ||
        d.doctor_name?.toLowerCase().includes(q) ||
        d.doctor_specialization?.toLowerCase().includes(q);
      return matchesSearch;
    });
  }, [doctorsForStaff, search]);

  function docById(id) {
    return doctors.find((d) => String(d.doctor_id) === String(id));
  }

  function normalizeAvailableDays(doc) {
    const raw = doc?.available_days ?? doc?.doctor_available_days ?? [];
    if (!raw) return [];
    const values = Array.isArray(raw) ? raw : String(raw).split(",");
    return [...new Set(
      values
        .map((v) => Number(v))
        .filter((v) => Number.isInteger(v) && v >= 1 && v <= 7)
    )].sort((a, b) => a - b);
  }

  function formatAvailableDays(doc) {
    const days = normalizeAvailableDays(doc);
    if (!days.length) return "No assigned days";
    return days.map((day) => DAYS[day - 1]).join(", ");
  }

  function isDoctorAvailableOnDay(docId, dayIndex) {
    const doc = docById(docId);
    const allowed = normalizeAvailableDays(doc);
    if (!allowed.length) return false;
    return allowed.includes(dayIndex + 1);
  }

  // Core / Secondary is the real doctor_type column now (0 = Core,
  // 1 = Secondary), set when the doctor was added in doctor.js — this is
  // just a read-only lookup, not something editable from this screen.
  function categoryOf(docId) {
    const doc = docById(docId);
    return Number(doc?.doctor_type) === 1 ? "secondary" : "core";
  }

  function dateForDay(day) {
    const dow = new Date(weekStart);
    dow.setDate(weekStart.getDate() + DAY_OFFSETS[day]);
    return isoDate(dow);
  }

  // Appointments that are ALREADY in appointments_tbl for this staff/day,
  // read straight from GET appointment/getbystaff/:staffId (real backend
  // data, scoped to the currently selected staff) — not localStorage. This
  // is what makes the Assign tab's calendar show the actual client name +
  // time for the chosen staff on each date.
  function savedListFor(staffId, day) {
    if (!staffId) return [];
    const dayIso = dateForDay(day);
    return staffAppointments
      .filter(
        (a) =>
          String(a.appoint_userid) === String(staffId) &&
          normalizeDateStr(a.appoint_appointmentdate) === dayIso &&
          !hiddenAppointIds.has(a.appoint_id)
      )
      .map((a) => ({
        id: a.appoint_clientautoid,
        clientName: a.appoint_clientname, // always shown as-is from the DB row
        timeFrom: a.appoint_timefrom,
        timeTo: a.appoint_timeto,
        saved: true,
        appointId: a.appoint_id,
      }));
  }

  // Combines what's actually saved in the DB (savedListFor) with anything
  // still staged locally and not yet pushed to the server (drafts, no
  // appoint_id). Drafts are the only thing that still lives in localStorage.
  function listFor(staffId, day) {
    const draftList = schedule[weekKey]?.[staffId]?.[day] || [];
    return [...savedListFor(staffId, day), ...draftList];
  }

  function updateList(staffId, day, updater) {
    setSchedule((prev) => {
      const next = { ...prev };
      next[weekKey] = { ...(next[weekKey] || {}) };
      next[weekKey][staffId] = { ...(next[weekKey][staffId] || {}) };
      const current = next[weekKey][staffId][day] || [];
      next[weekKey][staffId][day] = updater(current);
      return next;
    });
  }

  function isDoubleBooked(day, staffId, docId) {
    const dayIso = dateForDay(day);

    // Check real, already-saved appointments for every OTHER staff that day.
    const conflictInSaved = overviewData.some(
      (a) =>
        String(a.appoint_userid) !== String(staffId) &&
        normalizeDateStr(a.appoint_appointmentdate) === dayIso &&
        String(a.appoint_clientautoid) === String(docId) &&
        !hiddenAppointIds.has(a.appoint_id)
    );
    if (conflictInSaved) return true;

    // Then check other staff's unsaved local drafts for the same day.
    const weekData = schedule[weekKey] || {};
    for (const stId of Object.keys(weekData)) {
      if (stId === String(staffId)) continue;
      const list = weekData[stId]?.[day] || [];
      if (list.some((item) => item.id === docId)) return true;
    }
    return false;
  }

  // Generates a random time slot between 9:00 and 16:00 (1-hour appointments)
  // Returns a time string like "09:00", "10:30", etc.
  function getRandomTimeSlot() {
    const hours = [9, 10, 11, 12, 13, 14, 15, 16]; // 9 AM to 4 PM (last appt ends at 5 PM)
    const minutes = [0, 30];
    const randomHour = hours[Math.floor(Math.random() * hours.length)];
    const randomMin = minutes[Math.floor(Math.random() * minutes.length)];
    return `${String(randomHour).padStart(2, "0")}:${String(randomMin).padStart(2, "0")}`;
  }

  // Checks if a 1-hour time slot starting at timeStr on a given day is available
  // (no conflicts with other staff's appointments)
  function isTimeSlotAvailable(day, timeStr, staffId, docId) {
    if (!timeStr) return false;
    const dayIso = dateForDay(day);
    const [hStr, mStr] = timeStr.split(":");
    const startMinutes = parseInt(hStr, 10) * 60 + parseInt(mStr, 10);
    const endMinutes = startMinutes + 60; // 1-hour duration

    // Check for conflicts in already-saved appointments (other staff)
    const hasConflict = overviewData.some((a) => {
      if (String(a.appoint_userid) === String(staffId)) return false; // same staff, ok
      if (normalizeDateStr(a.appoint_appointmentdate) !== dayIso) return false; // different day
      if (String(a.appoint_clientautoid) !== String(docId)) return false; // different doctor
      if (hiddenAppointIds.has(a.appoint_id)) return false; // hidden appointments don't count

      // Parse existing appointment times
      const existingFrom = a.appoint_timefrom;
      const existingTo = a.appoint_timeto;
      if (!existingFrom || !existingTo) return false;

      const [existingHFrom, existingMFrom] = existingFrom.split(":");
      const [existingHTo, existingMTo] = existingTo.split(":");
      const existingStartMin = parseInt(existingHFrom, 10) * 60 + parseInt(existingMFrom, 10);
      const existingEndMin = parseInt(existingHTo, 10) * 60 + parseInt(existingMTo, 10);

      // Check if time ranges overlap
      return startMinutes < existingEndMin && endMinutes > existingStartMin;
    });

    return !hasConflict;
  }

  // Auto-allocate doctors to the week, respecting constraints
  // Returns { allocated, cores, secondary, message }
  async function autoAllocateDoctors(docIds, staffId, cap) {
    const staff = staffList.find((s) => String(s.staff_id) === String(staffId));
    if (!staff) return { allocated: 0, cores: 0, secondary: 0, message: "Staff not found" };

    // Separate and sort: Core doctors first (doctor_type === 0), then Secondary (1)
    const docsToAllocate = docIds
      .map((id) => docById(id))
      .filter((d) => d && String(d.doctor_servedby) === String(staffId));

    docsToAllocate.sort((a, b) => {
      const aIsCore = Number(a.doctor_type) === 0 ? 0 : 1;
      const bIsCore = Number(b.doctor_type) === 0 ? 0 : 1;
      return aIsCore - bIsCore; // Core (0) comes before Secondary (1)
    });

    const newSchedule = { ...schedule };
    const weekKeyVal = weekKey;
    if (!newSchedule[weekKeyVal]) newSchedule[weekKeyVal] = {};
    if (!newSchedule[weekKeyVal][staffId]) newSchedule[weekKeyVal][staffId] = {};

    let allocated = 0;
    let coreCount = 0;
    let secondaryCount = 0;

    for (const doc of docsToAllocate) {
      // Find available days (where count < cap)
      const availableDays = [];
      for (let day = 0; day < DAYS.length; day++) {
        const dayList = listFor(staffId, day);
        if (dayList.length < cap) {
          availableDays.push(day);
        }
      }

      if (availableDays.length === 0) {
        // No available days for this doctor
        continue;
      }

      // Pick a random available day
      const randomDay = availableDays[Math.floor(Math.random() * availableDays.length)];

      // Find an available time slot on that day
      let timeSlot = null;
      for (let attempt = 0; attempt < 10; attempt++) {
        const candidate = getRandomTimeSlot();
        if (isTimeSlotAvailable(randomDay, candidate, staffId, doc.doctor_id)) {
          timeSlot = candidate;
          break;
        }
      }

      if (!timeSlot) {
        // Couldn't find a free time slot on this day
        continue;
      }

      // Calculate end time (1 hour later)
      const [hStr, mStr] = timeSlot.split(":");
      const startMin = parseInt(hStr, 10) * 60 + parseInt(mStr, 10);
      const endMin = startMin + 60;
      const endHour = Math.floor(endMin / 60);
      const endMinute = endMin % 60;
      const timeTo = `${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(2, "0")}`;

      // Add to local schedule draft
      const newItem = {
        id: doc.doctor_id,
        timeFrom: timeSlot,
        timeTo: timeTo,
        saved: false,
        appointId: null,
      };

      if (!newSchedule[weekKeyVal][staffId][randomDay]) {
        newSchedule[weekKeyVal][staffId][randomDay] = [];
      }
      newSchedule[weekKeyVal][staffId][randomDay].push(newItem);

      allocated++;
      if (Number(doc.doctor_type) === 0) {
        coreCount++;
      } else {
        secondaryCount++;
      }
    }

    setSchedule(newSchedule);
    return { allocated, cores: coreCount, secondary: secondaryCount, message: "Success" };
  }

  function handleDrop(day) {
    setDragOverDay(null);
    if (!dragDocId || !selectedStaffId) return;

    const doc = docById(dragDocId);
    if (!doc) {
      setDragDocId(null);
      return;
    }

    if (!isDoctorAvailableOnDay(dragDocId, day)) {
      const allowedText = formatAvailableDays(doc);
      Swal.fire({
        title: "Doctor unavailable on this day",
        text: `${doc.doctor_name} is only available on ${allowedText}.`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      setDragDocId(null);
      return;
    }

    const already = listFor(selectedStaffId, day).some((item) => item.id === dragDocId);
    if (already) {
      setDragDocId(null);
      return;
    }
    const newItem = { id: dragDocId, timeFrom: null, timeTo: null, saved: false, appointId: null };
    updateList(selectedStaffId, day, (list) => [...list, newItem]);
    setDragDocId(null);
    setTimeModal({ day, item: newItem, doc });
  }

  function removeAssignment(day, item) {
    if (item.saved) {
      Swal.fire({
        title: "This appointment is already saved",
        text: "Removing it here only clears it from this board — the row will still exist in appointments_tbl. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remove from board",
      }).then((result) => {
        if (result.isConfirmed) {
          setHiddenAppointIds((prev) => {
            const next = new Set(prev);
            next.add(item.appointId);
            return next;
          });
        }
      });
      return;
    }
    updateList(selectedStaffId, day, (list) => list.filter((x) => x !== item));
  }

  async function saveTimeModal(from, to) {
    if (!timeModal) return;
    if (from && to && to <= from) {
      Swal.fire("Invalid range", '"To" must be after "From".', "warning");
      return;
    }
    if (!from || !to) {
      Swal.fire("Time required", "Both from and to times are required.", "warning");
      return;
    }

    const { day, item } = timeModal;

    // Already saved to the server — push the change to the DB right away
    // via the edit endpoint instead of only updating local state.
    if (item.saved && item.appointId) {
      setTimeSaving(true);
      try {
        const dow = new Date(weekStart);
        dow.setDate(weekStart.getDate() + DAY_OFFSETS[day]);

        await api.put(`appointment/editappointmenttime/${item.appointId}`, {
          time_from: from,
          time_to: to,
          appointment_date: isoDate(dow),
        });

        // Saved chips can be edited from either tab, but the Assign tab
        // reads from staffAppointments and the Overview tab reads from
        // overviewData — patch both so whichever one is showing this row
        // reflects the change immediately.
        const patch = {
          appoint_timefrom: from,
          appoint_timeto: to,
          appoint_appointmentdate: isoDate(dow),
        };
        setStaffAppointments((prev) =>
          prev.map((a) => (a.appoint_id === item.appointId ? { ...a, ...patch } : a))
        );
        setOverviewData((prev) =>
          prev.map((a) => (a.appoint_id === item.appointId ? { ...a, ...patch } : a))
        );
        setTimeModal(null);
        Swal.fire("Updated", "Appointment time updated.", "success");
      } catch (err) {
        console.error("editappointmenttime error:", err);
        const detail =
          err.response?.data?.message ||
          err.response?.data?.error ||
          (err.request ? "No response from server." : err.message) ||
          "Failed to update appointment time.";
        Swal.fire("Error", detail, "error");
      } finally {
        setTimeSaving(false);
      }
      return;
    }

    // Not saved yet — just stage locally; it goes up with the next
    // "Save schedule" bulk call.
    updateList(selectedStaffId, day, (list) =>
      list.map((x) => (x === item ? { ...x, timeFrom: from, timeTo: to } : x))
    );
    setTimeModal(null);
  }

  function clearTime() {
    if (!timeModal) return;
    if (timeModal.item.saved) {
      Swal.fire(
        "Can't clear a saved time",
        "This appointment is already saved and needs a time. Enter a new time instead of clearing it.",
        "info"
      );
      return;
    }
    updateList(selectedStaffId, timeModal.day, (list) =>
      list.map((x) => (x === timeModal.item ? { ...x, timeFrom: null, timeTo: null } : x))
    );
    setTimeModal(null);
  }

  // Permanently deletes an appointment via POST /appointment/deleteappointment/:id
  // (see appointments.js). Only applies to saved chips (ones with a real
  // appointId) — unsaved drafts are just removed from local state via the
  // existing "x" / removeAssignment flow instead.
  async function deleteAppointment() {
    if (!timeModal || !timeModal.item.appointId) return;
    const appointId = timeModal.item.appointId;

    const result = await Swal.fire({
      title: "Delete this appointment?",
      text: "This permanently removes it from appointments_tbl. This can't be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#C1543A",
    });
    if (!result.isConfirmed) return;

    setTimeSaving(true);
    try {
      await api.post(`appointment/deleteappointment/${appointId}`);

      // Remove it from both feeds so whichever tab is showing it updates
      // immediately (same dual-update reasoning as saveTimeModal above).
      setStaffAppointments((prev) => prev.filter((a) => a.appoint_id !== appointId));
      setOverviewData((prev) => prev.filter((a) => a.appoint_id !== appointId));
      setHiddenAppointIds((prev) => {
        if (!prev.has(appointId)) return prev;
        const next = new Set(prev);
        next.delete(appointId);
        return next;
      });

      setTimeModal(null);
      Swal.fire("Deleted", "Appointment deleted.", "success");
    } catch (err) {
      console.error("deleteappointment error:", err);
      const detail =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (err.request ? "No response from server." : err.message) ||
        "Failed to delete appointment.";
      Swal.fire("Error", detail, "error");
    } finally {
      setTimeSaving(false);
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function resetWeekForStaff() {
    const result = await Swal.fire({
      title: "Clear this staff member's week?",
      text: `All appointments for ${selectedStaff?.staff_name || "this staff"} in ${fmtWeekLabel(weekStart)} will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Clear",
    });
    if (!result.isConfirmed) return;
    setSchedule((prev) => {
      const next = { ...prev };
      if (next[weekKey]) {
        next[weekKey] = { ...next[weekKey] };
        delete next[weekKey][selectedStaffId];
      }
      return next;
    });
  }

  function changeWeek(deltaDays) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(getMonday(d));
  }

  // Loads the CURRENTLY SELECTED staff's appointments from
  // GET appointment/getbystaff/:staffId — this is what the Assign tab's
  // calendar renders (via savedListFor/listFor). All of that staff's rows
  // come back regardless of date; day-matching happens client-side the
  // same way fetchOverview does below.
  async function fetchStaffAppointments(staffId) {
    if (!staffId) {
      setStaffAppointments([]);
      return;
    }
    setStaffAppointmentsLoading(true);
    try {
      const res = await api.get(`appointment/getbystaff/${staffId}`);
      setStaffAppointments(res.data.status === "Success" ? res.data.data || [] : []);
    } catch (err) {
      console.error("getbystaff error:", err);
      Swal.fire("Error", err.response?.data?.message || "Couldn't load this staff member's appointments.", "error");
      setStaffAppointments([]);
    } finally {
      setStaffAppointmentsLoading(false);
    }
  }

  // Re-fetch every time a different staff is chosen in the dropdown —
  // "on choosing corresponding staff" the calendar should show THEIR
  // appointments from the DB, not whatever was left over from before.
  useEffect(() => {
    fetchStaffAppointments(selectedStaffId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId]);

  // Loads every visible staff member's appointments (all of them, not just
  // the current week) — backs the "All staff overview" tab. Read-only
  // rollup, fetched straight from the DB rather than derived from the
  // drag/drop board's local state, since other admins' saves need to show
  // up here too. The current week's days are then sliced out of this full
  // set client-side (see the dayAppts filter in the overview table below),
  // so switching weeks doesn't require a new request and there's no SQL
  // date-boundary comparison that could silently drop a day's worth of rows.
  async function fetchOverview() {
    setOverviewLoading(true);
    try {
      const res = await api.get("appointment/getweekoverview");
      setOverviewData(res.data.status === "Success" ? res.data.appointments || [] : []);
    } catch (err) {
      console.error("getweekoverview error:", err);
      Swal.fire("Error", err.response?.data?.message || "Couldn't load the appointment overview.", "error");
      setOverviewData([]);
    } finally {
      setOverviewLoading(false);
    }
  }

  async function duplicateFourWeekSchedule() {
    if (!selectedStaffId) {
      Swal.fire("Select staff", "Choose a staff member first.", "info");
      return;
    }

    const sourceStartDate = startOfLocalDay(weekStart);
    const sourceEndDate = addLocalDays(sourceStartDate, 27);
    const targetStartDate = addLocalDays(sourceStartDate, 28);
    const targetEndDate = addLocalDays(sourceStartDate, 55);

    const result = await Swal.fire({
      title: "Duplicate 4-week schedule?",
      text: `This will copy appointments for ${selectedStaff?.staff_name || "this staff"} from ${isoDate(sourceStartDate)} to ${isoDate(sourceEndDate)} into ${isoDate(targetStartDate)} to ${isoDate(targetEndDate)}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Duplicate",
      confirmButtonColor: "#2E6E5E",
    });

    if (!result.isConfirmed) return;

    setDuplicatingSchedule(true);
    try {
      const res = await api.post("appointment/duplicate4weeksschedule", {
        staff_id: selectedStaffId,
        source_start_date: isoDate(sourceStartDate),
        source_end_date: isoDate(sourceEndDate),
      });

      if (res.data.status === "Success") {
        await Promise.all([fetchStaffAppointments(selectedStaffId), fetchOverview()]);
        Swal.fire(
          "Duplicated",
          res.data.message || "4-week schedule duplicated successfully.",
          "success"
        );
      } else {
        Swal.fire("Error", res.data.message || "Could not duplicate schedule.", "error");
      }
    } catch (err) {
      console.error("duplicate4weeksschedule error:", err);
      const detail =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (err.request ? "No response from server." : err.message) ||
        "Failed to duplicate schedule.";
      Swal.fire("Error", detail, "error");
    } finally {
      setDuplicatingSchedule(false);
    }
  }

  // Fetch once on mount — this backs the Staff overview tab, and is also
  // used for cross-staff double-booking checks from the Assign tab.
  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch whenever the overview tab is opened, so it stays fresh if
  // other staff/admins saved appointments elsewhere in the meantime.
  useEffect(() => {
    if (viewMode === "overview") fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Turns one { id, timeFrom, timeTo } schedule item into the shape
  // /addappointment(bulk) expects. Returns null if it's not ready to save
  // yet (no time set, staff/doctor missing).
  function buildAppointmentPayload(staffId, day, item) {
    const staff = staffList.find((s) => String(s.staff_id) === String(staffId));
    const doc = docById(item.id);
    if (!staff || !doc || !item.timeFrom || !item.timeTo) return null;

    const dow = new Date(weekStart);
    dow.setDate(weekStart.getDate() + DAY_OFFSETS[day]);

    return {
      staff_id: staff.staff_id,
      staff_name: staff.staff_name,
      client_type: DOCTOR_CLIENT_TYPE,
      client_id: doc.doctor_id,
      client_name: doc.doctor_name,
      appointment_date: isoDate(dow),
      time_from: item.timeFrom,
      time_to: item.timeTo,
      userid: loggedInUserId,
      assigned_userid: loggedInUserId,
      assigned_staffname: loggedInUserName,
    };
  }

  // Sends every not-yet-saved, time-set assignment for the selected staff
  // (across all days in the visible week) to the server in one request.
  // Covers both cases from the requirement: several doctors dropped on one
  // day, or one doctor dropped on several days — all go up together.
  async function saveScheduleToServer() {
    if (!selectedStaffId) {
      Swal.fire("Select staff", "Choose a staff member first.", "info");
      return;
    }
    if (!loggedInUserId) {
      Swal.fire("Error", "Couldn't identify the logged-in user from the session token.", "error");
      return;
    }

    // pending: [{ day, item, payload }]
    const pending = [];
    for (let day = 0; day < DAYS.length; day++) {
      listFor(selectedStaffId, day).forEach((item) => {
        if (item.saved) return;
        const payload = buildAppointmentPayload(selectedStaffId, day, item);
        if (payload) pending.push({ day, item, payload });
      });
    }

    if (pending.length === 0) {
      Swal.fire(
        "Nothing to save",
        "Assign at least one doctor to a day before saving the schedule.",
        "info"
      );
      return;
    }

    setSaving(true);
    try {
      const res = await api.post("appointment/addappointmentbulk", {
        appointments: pending.map((p) => p.payload),
      });

      if (res.data.status === "Success") {
        const insertIds = res.data.insertIds; // insertIds[idx] maps 1:1 to pending[idx]

        // These items are now rows in appointments_tbl, so drop them out of
        // the local draft schedule — staffAppointments/overviewData are
        // their source from here on.
        setSchedule((prev) => {
          const next = { ...prev };
          next[weekKey] = { ...(next[weekKey] || {}) };
          next[weekKey][selectedStaffId] = { ...(next[weekKey][selectedStaffId] || {}) };
          pending.forEach((p) => {
            const dayList = (next[weekKey][selectedStaffId][p.day] || []).filter(
              (x) => x !== p.item
            );
            next[weekKey][selectedStaffId][p.day] = dayList;
          });
          return next;
        });

        // Reflect the new rows immediately, sourced the same way a page
        // reload would pull them (via getbystaff / getweekoverview), so the
        // calendar shows the DB state right away without a full refetch.
        // Pushed into both feeds since the Assign tab reads staffAppointments
        // and the Overview tab reads overviewData.
        const newRows = pending.map((p, idx) => ({
          appoint_id: insertIds && insertIds[idx] != null ? insertIds[idx] : null,
          appoint_userid: selectedStaffId,
          appoint_staffname: selectedStaff?.staff_name,
          appoint_clientautoid: p.item.id,
          appoint_clientname: docById(p.item.id)?.doctor_name || "",
          appoint_clienttype: DOCTOR_CLIENT_TYPE,
          appoint_appointmentdate: p.payload.appointment_date,
          appoint_timefrom: p.payload.time_from,
          appoint_timeto: p.payload.time_to,
          appoint_status: null,
          appointapproval_status: null,
        }));
        setStaffAppointments((prev) => [...prev, ...newRows]);
        setOverviewData((prev) => [...prev, ...newRows]);

        Swal.fire(
          "Saved",
          `${pending.length} appointment${pending.length > 1 ? "s" : ""} added to the appointments table.`,
          "success"
        );
      } else {
        Swal.fire("Error", "Could not save the schedule.", "error");
      }
    } catch (err) {
      console.error("addappointmentbulk error:", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to save schedule.", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rs-root">
      <style>{`
        .rs-root{
          font-family:'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color:#1E2A2A;
          background:#fff;
          border:1px solid #E4EAE9;
          border-radius:12px;
          overflow:hidden;
          display:flex;
5          height:640px;
          max-height:640px;
          min-height:640px;
          min-width:0;
        }

        /* ---------- Left: doctor pool ---------- */
        .rs-pool{
          width:230px; min-width:230px;
          background:#fff;
          border-right:1px solid #E4EAE9;
          display:flex; flex-direction:column;
          height:100%;
          max-height:100%;
          min-height:0;
        }
        .rs-pool-header{ padding:16px 16px 10px 16px; }
        .rs-pool-header h5{ margin:0; font-weight:700; font-size:17px; color:#1E2A2A; }
        .rs-pool-header .sub{ margin-top:2px; font-size:12px; color:#8A9694; }

        .rs-staff-select-wrap{ padding:0 16px 12px 16px; }
        .rs-staff-select-wrap label{
          display:block; font-size:11px; font-weight:600; color:#8A9694;
          text-transform:uppercase; letter-spacing:.03em; margin-bottom:4px;
        }
        .rs-staff-select-wrap select{
          width:100%; font-size:13px; padding:7px 10px; border-radius:8px;
          border:1px solid #DCE4E2; background:#fff; color:#1E2A2A;
        }

        .rs-search{ padding:0 16px 12px 16px; }
        .rs-search-box{
          display:flex; align-items:center; gap:8px;
          border:1px solid #DCE4E2; border-radius:9px; padding:8px 12px;
          background:#FAFCFB;
        }
        .rs-search-box svg{ color:#9BA9A8; flex-shrink:0; }
        .rs-search-box input{
          border:none; outline:none; background:transparent; font-size:13px;
          width:100%; color:#1E2A2A;
        }
        .rs-search-box input::placeholder{ color:#9BA9A8; }

        .rs-pool-list{
          flex:1 1 auto;
          min-height:0;
          overflow-y:auto;
          overflow-x:hidden;
          padding:0 14px 14px 14px;
          display:flex; flex-direction:column; gap:8px;
          overscroll-behavior: contain;
        }
        .rs-doc-card{
          padding:11px 13px; background:#EAF1FB; border:1px solid #D6E4F5; border-radius:10px;
          display:flex; align-items:center; gap:8px; cursor:grab;
          transition:border-color .15s, box-shadow .15s;
        }
        .rs-doc-card:hover{ border-color:#B9CDEA; box-shadow:0 1px 4px rgba(30,42,42,.06); }
        .rs-doc-card:active{ cursor:grabbing; }
        .rs-doc-card .cat-badge{
          font-size:9.5px; font-weight:700; letter-spacing:.03em; text-transform:uppercase;
          padding:2px 7px; border-radius:20px; flex-shrink:0; line-height:1.4;
        }
        .rs-doc-card .cat-badge.core{ background:#2E6E5E; color:#fff; }
        .rs-doc-card .cat-badge.secondary{ background:#2E6E5E; color:#fff; }
        .rs-doc-card .nm{
          font-weight:600; font-size:13.5px; color:#1E2A2A; flex:1;
          overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
        }
        .rs-doc-card .spec{ font-size:12px; color:#8A9694; flex-shrink:0; }
        .rs-empty-hint{ font-size:12px; color:#9BA9A8; text-align:center; margin-top:20px; padding:0 10px; }

        /* ---------- Right: schedule ---------- */
        .rs-main{ flex:1; display:flex; flex-direction:column; min-width:0; min-height:0; }
        .rs-tabs-row{
          padding:12px 22px 0 22px; border-bottom:1px solid #E4EAE9;
        }
        .rs-toolbar{
          display:flex; align-items:center; gap:14px; padding:14px 22px;
          border-bottom:1px solid #E4EAE9; flex-wrap:wrap; overflow-x:visible;
        }
        .rs-tabs{ display:inline-flex; gap:4px; background:#F2F6F4; border-radius:8px; padding:3px; margin-bottom:10px; }
        .rs-tabs button{
          border:none; background:transparent; padding:6px 12px; font-size:12.5px; font-weight:600;
          color:#5B6E70; border-radius:6px; cursor:pointer; white-space:nowrap;
        }
        .rs-tabs button.active{ background:#2E6E5E; color:#fff; }
        .rs-overview-staffcol{ width:150px; }
        .rs-overview-staffname{
          font-weight:600; font-size:13px; color:#1E2A2A; vertical-align:middle; white-space:nowrap;
        }
        .rs-toolbar-divider{ width:1px; height:16px; background:#E4EAE9; flex-shrink:0; }
        .rs-week-nav{ display:flex; align-items:center; gap:6px; flex-shrink:0; }
        .rs-week-nav button{
          width:20px; height:20px; border-radius:5px; border:none;
          background:transparent; cursor:pointer; color:#5B6E70; display:flex;
          align-items:center; justify-content:center; font-size:14px; padding:0; line-height:1;
        }
        .rs-week-nav button:hover{ color:#2E6E5E; }
        .rs-week-label{ font-size:13px; font-weight:600; color:#1E2A2A; white-space:nowrap; }
        .rs-cap{ display:flex; align-items:center; gap:7px; font-size:13px; color:#5B6E70; white-space:nowrap; flex-shrink:0; }
        .rs-cap input{
          width:42px; padding:4px 6px; border:1px solid #DCE4E2; border-radius:6px;
          text-align:center; font-size:13px;
        }
        .rs-saved-note{ margin-left:auto; font-size:12px; color:#8FAE9F; white-space:nowrap; }

        .rs-table-scroll{ flex:1; overflow:auto; padding:18px 22px; }
        .rs-table{
          width:100%; min-width:780px; border-collapse:separate; border-spacing:0;
          border:1px solid #E4EAE9; border-radius:10px; overflow:hidden;
          table-layout:fixed;
        }
        .rs-table thead th{
          background:#2E5B57; color:#fff; font-weight:600; font-size:13px;
          text-align:left; padding:10px 14px;
        }
        .rs-table tbody td{
          border-top:1px solid #E4EAE9; border-left:1px solid #E4EAE9;
          vertical-align:top; padding:12px 12px;
        }
        .rs-table tbody td:first-child{ border-left:none; }

        .rs-day-cell{ min-width:150px; }
        .rs-day-cell.drag-over{ background:#EEF6F2; }
        .rs-progress-track{
          height:4px; width:100%; background:#EAF0EE; border-radius:2px; overflow:hidden; margin-bottom:6px;
        }
        .rs-progress-fill{ height:100%; background:#2E6E5E; border-radius:2px; }
        .rs-progress-fill.over{ background:#C1543A; }
        .rs-count{
          font-size:12px; font-weight:600; color:#2E6E5E; margin-bottom:8px; display:block;
        }
        .rs-count.over{ color:#C1543A; }
        .rs-chip-list{ display:flex; flex-direction:column; gap:6px; }
        .rs-chip{
          display:flex; align-items:center; gap:5px; background:#F2F7F5;
          border:1px solid #DCE4E2; border-radius:7px; padding:5px 7px; font-size:12px;
        }
        .rs-chip.conflict{ background:#FBF0E2; border-color:#D79A5C; }
        .rs-chip.saved{ background:#EAF5EF; border-color:#8FC7A6; }
        .rs-chip.status-complete{ background:#E8F7EE; border-color:#4CAF7A; }
        .rs-chip.status-incomplete{ background:#FDECEC; border-color:#E26A6A; }
        .rs-chip .saved-check{ color:#2E6E5E; margin-left:4px; flex-shrink:0; }
        .rs-chip.status-complete .saved-check{ color:#2E6E5E; }
        .rs-chip.status-incomplete .saved-check{ color:#C1543A; }
        .rs-chip .cat-dot{ font-size:9px; flex-shrink:0; }
        .rs-chip .cat-dot.core{ color:#2E6E5E; }
        .rs-chip .cat-dot.secondary{ color:#B9C4C2; }
        .rs-chip .nm-wrap{
          flex:1; overflow:hidden; cursor:pointer; display:flex; flex-direction:column; min-width:0;
        }
        .rs-chip .nm-wrap:hover .nm{ text-decoration:underline; }
        .rs-chip .nm{
          overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-weight:600;
        }
        .rs-chip .tm{
          font-size:10.5px; color:#5B6E70; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
        }
        .rs-chip .x{ cursor:pointer; color:#9BA9A8; flex-shrink:0; display:flex; }
        .rs-chip .x:hover{ color:#C1543A; }
        .rs-drop-hint{ font-size:11px; color:#B7C2C0; padding:4px 0; }

        /* ---------- Responsive ---------- */
        @media (max-width: 900px){
          .rs-root{ flex-direction:column; min-height:0; }
          .rs-pool{
            width:100%; min-width:0;
            border-right:none; border-bottom:1px solid #E4EAE9;
          }
          .rs-pool-list{ max-height:260px; }
          .rs-main{ min-width:0; }
          .rs-table-scroll{ padding:14px; }
        }
        @media (max-width: 520px){
          .rs-pool-header{ padding:14px 14px 8px 14px; }
          .rs-pool-header h5{ font-size:15px; }
          .rs-staff-select-wrap, .rs-search{ padding-left:14px; padding-right:14px; }
          .rs-pool-list{ padding:0 12px 12px 12px; max-height:220px; }
          .rs-tabs-row{ padding:10px 14px 0 14px; }
          .rs-toolbar{ padding:12px 14px; gap:10px; }
          .rs-table-scroll{ padding:12px; }
          .rs-saved-note{ display:none; }
        }
      `}</style>

      {/* ---------------- Left: doctor pool (assign mode only) ---------------- */}
      {viewMode === "assign" && (
      <div className="rs-pool">
        <div className="rs-pool-header">
          <h5>Doctor pool</h5>
          <div className="sub">
            {doctorsFiltered.length} of {doctorsForStaff.length} doctors shown
          </div>
        </div>

        <div className="rs-staff-select-wrap">
          <label>Staff</label>
          <select
            value={selectedStaffId}
            onChange={(e) => setSelectedStaffId(e.target.value)}
            disabled={loadingStaff}
          >
            {loadingStaff && <option>Loading staff…</option>}
            {!loadingStaff && staffList.length === 0 && <option>No staff found</option>}
            {!loadingStaff && staffList.length > 0 && <option value="">Select staff</option>}
            {staffList.map((s) => (
              <option key={s.staff_id} value={s.staff_id}>
                {s.staff_name} {s.district_name ? `— ${s.district_name}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="rs-search">
          <div className="rs-search-box">
            <Icon.Search size={14} />
            <input
              type="text"
              placeholder="Search doctors…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="rs-pool-list">
          {loadingDoctors && <div className="rs-empty-hint">Loading doctors…</div>}
          {!loadingDoctors && doctorsFiltered.length === 0 && (
            <div className="rs-empty-hint">
              {doctorsForStaff.length === 0
                ? "No doctors are served by this staff member yet."
                : "No doctors match your search/filter."}
            </div>
          )}
          {doctorsFiltered.map((d) => {
            const cat = categoryOf(d.doctor_id);
            const availableDays = formatAvailableDays(d);
            return (
              <div
                key={d.doctor_id}
                className="rs-doc-card"
                draggable
                onDragStart={() => setDragDocId(d.doctor_id)}
                onDragEnd={() => setDragDocId(null)}
              >
                <span
                  className={`cat-badge ${cat}`}
                  title={cat === "core" ? "Core doctor" : "Secondary doctor"}
                >
                  {cat === "core" ? "Core" : "Sec"}
                </span>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                  <span className="nm">{d.doctor_name}</span>
                  {d.doctor_specialization && <span className="spec">{d.doctor_specialization}</span>}
                  <span style={{ fontSize: 11, color: "#5B6E70", marginTop: 4, whiteSpace: "normal" }}>
                    Available: {availableDays}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* ---------------- Right: weekly schedule table ---------------- */}
      <div className="rs-main">
        <div className="rs-tabs-row">
          <div className="rs-tabs">
            <button
              className={viewMode === "assign" ? "active" : ""}
              onClick={() => setViewMode("assign")}
            >
              Assign
            </button>
            <button
              className={viewMode === "overview" ? "active" : ""}
              onClick={() => setViewMode("overview")}
            >
              All staff overview
            </button>
            <button
              className={viewMode === "autoassign" ? "active" : ""}
              onClick={() => setViewMode("autoassign")}
            >
              Auto-assign
            </button>
          </div>
        </div>

        <div className="rs-toolbar">
          <div className="rs-week-nav">
            <button onClick={() => changeWeek(-7)}>‹</button>
            <div className="rs-week-label">{fmtWeekLabel(weekStart)}</div>
            <button onClick={() => changeWeek(7)}>›</button>
          </div>
          {viewMode === "assign" && (
            <>
              <div className="rs-toolbar-divider" />
              <div className="rs-cap">
                Daily target:
                <input type="number" min="1" value={cap} onChange={(e) => setCap(parseInt(e.target.value, 10) || 5)} />
                visits / staff
              </div>
              <Button
                size="sm"
                style={{ marginLeft: "auto", background: "#2E6E5E", borderColor: "#2E6E5E" }}
                onClick={saveScheduleToServer}
                disabled={!selectedStaffId || saving || duplicatingSchedule}
              >
                {saving ? "Saving…" : "Save schedule"}
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={duplicateFourWeekSchedule}
                disabled={!selectedStaffId || saving || duplicatingSchedule}
              >
                {duplicatingSchedule ? "Duplicating…" : "Duplicate 4 weeks"}
              </Button>
            </>
          )}
          {/* <Button size="sm" variant="outline-secondary" onClick={resetWeekForStaff} disabled={!selectedStaffId}>
            Clear week
          </Button> */}
        </div>

        <div className="rs-table-scroll">
          {viewMode === "assign" ? (
          <table className="rs-table">
            <thead>
              <tr>
                {DAYS.map((label, day) => {
                  const dow = new Date(weekStart);
                  dow.setDate(weekStart.getDate() + DAY_OFFSETS[day]);
                  return <th key={day}>{label} {dow.getDate()}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              <tr className="rs-staff-row">
                {DAYS.map((_, day) => {
                  const list = listFor(selectedStaffId, day);
                  const over = list.length > cap;
                  const pct = Math.min(100, Math.round((list.length / cap) * 100));
                  return (
                    <td
                      key={day}
                      className={`rs-day-cell ${dragOverDay === day ? "drag-over" : ""}`}
                      onDragOver={(e) => { e.preventDefault(); if (selectedStaffId) setDragOverDay(day); }}
                      onDragLeave={() => setDragOverDay((d) => (d === day ? null : d))}
                      onDrop={(e) => { e.preventDefault(); handleDrop(day); }}
                    >
                      <div className="rs-progress-track">
                        <div className={`rs-progress-fill ${over ? "over" : ""}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`rs-count ${over ? "over" : ""}`}>{list.length} / {cap}</span>
                      <div className="rs-chip-list">
                        {list.length === 0 && (
                          <div className="rs-drop-hint">
                            {selectedStaffId ? "Drop a doctor here" : "Select a staff member first"}
                          </div>
                        )}
                        {list.map((item) => {
                          const doc = docById(item.id) || {
                            doctor_id: item.id,
                            doctor_name: item.clientName || "Unknown",
                          };
                          const conflict = isDoubleBooked(day, selectedStaffId, item.id);
                          const from = fmtTime(item.timeFrom);
                          const to = fmtTime(item.timeTo);
                          const timeLabel = from && to ? `${from}–${to}` : from;
                          return (
                            <div
                              key={doc.doctor_id + JSON.stringify(item)}
                              className={`rs-chip ${conflict ? "conflict" : ""} ${item.saved ? "saved" : ""} ${item.saved ? appointmentStatusClass(item.appoint_status) : ""}`}
                            >
                              <span
                                className={`cat-dot ${categoryOf(doc.doctor_id)}`}
                                title={categoryOf(doc.doctor_id) === "core" ? "Core doctor" : "Secondary doctor"}
                              >
                                {categoryOf(doc.doctor_id) === "core" ? "●" : "○"}
                              </span>
                              <span
                                className="nm-wrap"
                                onClick={() => setTimeModal({ day, item, doc, source: "assign" })}
                                title="Click to view / edit appointment time"
                              >
                                <span className="nm">
                                  {doc.doctor_name}
                                  {item.saved && <Icon.CheckCircleFill size={10} className="saved-check" />}
                                </span>
                                {timeLabel && <span className="tm">{timeLabel}</span>}
                              </span>
                              <span className="x" onClick={() => removeAssignment(day, item)}>
                                <Icon.X size={12} />
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
          ) : viewMode === "overview" ? (
          <table className="rs-table rs-overview-table">
            <thead>
              <tr>
                <th className="rs-overview-staffcol">Staff</th>
                {DAYS.map((label, day) => {
                  const dow = new Date(weekStart);
                  dow.setDate(weekStart.getDate() + DAY_OFFSETS[day]);
                  return <th key={day}>{label} {dow.getDate()}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {overviewLoading && (
                <tr><td colSpan={DAYS.length + 1} className="rs-empty-hint">Loading overview…</td></tr>
              )}
              {!overviewLoading && staffList.length === 0 && (
                <tr><td colSpan={DAYS.length + 1} className="rs-empty-hint">No staff found.</td></tr>
              )}
              {!overviewLoading && staffList.map((staff) => (
                <tr key={staff.staff_id}>
                  <td className="rs-overview-staffname">{staff.staff_name}</td>
                  {DAYS.map((_, day) => {
                    const dow = new Date(weekStart);
                    dow.setDate(weekStart.getDate() + DAY_OFFSETS[day]);
                    const dayIso = isoDate(dow);
                    const dayAppts = overviewData.filter(
                      (a) =>
                        String(a.appoint_userid) === String(staff.staff_id) &&
                        normalizeDateStr(a.appoint_appointmentdate) === dayIso
                    );
                    return (
                      <td key={day} className="rs-day-cell">
                        <span className="rs-count">{dayAppts.length} appt{dayAppts.length === 1 ? "" : "s"}</span>
                        <div className="rs-chip-list">
                          {dayAppts.length === 0 && <div className="rs-drop-hint">No appointments</div>}
                          {dayAppts.map((appt) => {
                            const from = fmtTime(appt.appoint_timefrom);
                            const to = fmtTime(appt.appoint_timeto);
                            const timeLabel = from && to ? `${from}–${to}` : from;
                            return (
                              <div
                                key={appt.appoint_id}
                                className={`rs-chip saved ${appointmentStatusClass(appt.appoint_status)}`}
                              >
                                <span
                                  className="nm-wrap"
                                  onClick={() =>
                                    setTimeModal({
                                      day,
                                      doc: { doctor_name: appt.appoint_clientname },
                                      item: {
                                        id: appt.appoint_clientautoid,
                                        timeFrom: appt.appoint_timefrom,
                                        timeTo: appt.appoint_timeto,
                                        saved: true,
                                        appointId: appt.appoint_id,
                                      },
                                      source: "overview",
                                    })
                                  }
                                  title="Click to view / edit appointment time"
                                >
                                  <span className="nm">
                                    {appt.appoint_clientname}
                                    <Icon.CheckCircleFill size={10} className="saved-check" />
                                  </span>
                                  {timeLabel && <span className="tm">{timeLabel}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          ) : (
          <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ fontWeight: 600, fontSize: "14px", color: "#1E2A2A", whiteSpace: "nowrap" }}>Staff:</label>
                <select
                  value={selectedStaffId}
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                  disabled={loadingStaff || autoAssigning}
                  style={{
                    fontSize: "13px",
                    padding: "7px 10px",
                    borderRadius: "8px",
                    border: "1px solid #DCE4E2",
                    backgroundColor: "#fff",
                    color: "#1E2A2A",
                  }}
                >
                  {loadingStaff && <option>Loading staff…</option>}
                  {!loadingStaff && staffList.length === 0 && <option>No staff found</option>}
                  {!loadingStaff && staffList.length > 0 && <option value="">Select staff</option>}
                  {staffList.map((s) => (
                    <option key={s.staff_id} value={s.staff_id}>
                      {s.staff_name} {s.district_name ? `— ${s.district_name}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                <button 
                  onClick={() => changeWeek(-7)} 
                  disabled={autoAssigning}
                  style={{
                    width: "20px", height: "20px", borderRadius: "5px", border: "none",
                    background: "transparent", cursor: "pointer", color: "#5B6E70",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px",
                  }}
                >
                  ‹
                </button>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#1E2A2A", minWidth: "200px", textAlign: "center" }}>
                  {fmtWeekLabel(weekStart)}
                </div>
                <button 
                  onClick={() => changeWeek(7)}
                  disabled={autoAssigning}
                  style={{
                    width: "20px", height: "20px", borderRadius: "5px", border: "none",
                    background: "transparent", cursor: "pointer", color: "#5B6E70",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px",
                  }}
                >
                  ›
                </button>
              </div>

              <Button
                size="sm"
                style={{ marginLeft: "auto", background: "#2E6E5E", borderColor: "#2E6E5E" }}
                onClick={async () => {
                  if (!selectedStaffId) {
                    Swal.fire("Select staff", "Choose a staff member first.", "info");
                    return;
                  }
                  if (selectedDoctorIds.size === 0) {
                    Swal.fire("Select doctors", "Choose at least one doctor to auto-assign.", "info");
                    return;
                  }

                  const selectedArray = Array.from(selectedDoctorIds);
                  const coreCount = selectedArray.filter((id) => Number(docById(id)?.doctor_type) === 0).length;
                  const secondaryCount = selectedArray.length - coreCount;

                  setAutoAssignModal({
                    coreCount,
                    secondaryCount,
                    totalCount: selectedArray.length,
                    docIds: selectedArray,
                  });
                }}
                disabled={!selectedStaffId || selectedDoctorIds.size === 0 || autoAssigning}
              >
                {autoAssigning ? "Assigning…" : "Auto-assign selected"}
              </Button>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: "10px",
              border: "1px solid #E4EAE9",
              borderRadius: "10px",
              padding: "15px",
              background: "#FAFCFB",
              maxHeight: "500px",
              overflow: "auto",
            }}>
              {loadingDoctors && <div style={{ gridColumn: "1/-1", textAlign: "center", color: "#9BA9A8" }}>Loading doctors…</div>}
              {!loadingDoctors && doctorsForStaff.length === 0 && (
                <div style={{ gridColumn: "1/-1", textAlign: "center", color: "#9BA9A8" }}>
                  No doctors are served by this staff member yet.
                </div>
              )}
              {doctorsForStaff.map((d) => {
                const isSelected = selectedDoctorIds.has(d.doctor_id);
                const isCore = Number(d.doctor_type) === 0;
                return (
                  <label
                    key={d.doctor_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 12px",
                      background: isSelected ? "#EAF5EF" : "#fff",
                      border: isSelected ? "1px solid #8FC7A6" : "1px solid #DCE4E2",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const next = new Set(selectedDoctorIds);
                        if (e.target.checked) {
                          next.add(d.doctor_id);
                        } else {
                          next.delete(d.doctor_id);
                        }
                        setSelectedDoctorIds(next);
                      }}
                      disabled={autoAssigning}
                      style={{ cursor: "pointer" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1E2A2A",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {d.doctor_name}
                      </div>
                      <div style={{
                        fontSize: "11px",
                        color: "#8A9694",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {isCore ? "Core" : "Secondary"}
                        {d.doctor_specialization && ` • ${d.doctor_specialization}`}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {selectedDoctorIds.size > 0 && (
              <div style={{
                fontSize: "12px",
                color: "#8A9694",
                padding: "10px 12px",
                background: "#F2F6F4",
                borderRadius: "8px",
              }}>
                Selected: {selectedDoctorIds.size} doctor{selectedDoctorIds.size !== 1 ? "s" : ""} ({Array.from(selectedDoctorIds).filter((id) => Number(docById(id)?.doctor_type) === 0).length} Core, {selectedDoctorIds.size - Array.from(selectedDoctorIds).filter((id) => Number(docById(id)?.doctor_type) === 0).length} Secondary)
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      <Modal show={!!autoAssignModal} onHide={() => setAutoAssignModal(null)} centered size="sm" backdrop={autoAssigning ? "static" : true}>
        <Modal.Header closeButton={!autoAssigning}>
          <Modal.Title style={{ fontSize: 16 }}>Auto-assign doctors?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 13, color: "#5B6E70", lineHeight: 1.6 }}>
            <p>You are about to auto-assign the following doctors to the week of {fmtWeekLabel(weekStart)}:</p>
            <div style={{
              background: "#F2F6F4",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "12px",
              fontSize: "13px",
              fontWeight: 600,
            }}>
              <div>🟢 Core doctors: {autoAssignModal?.coreCount || 0}</div>
              <div>🔵 Secondary doctors: {autoAssignModal?.secondaryCount || 0}</div>
              <div style={{ marginTop: "6px", fontWeight: "normal" }}>Total: {autoAssignModal?.totalCount || 0}</div>
            </div>
            <p style={{ fontSize: 12, color: "#8A9694", marginBottom: 0 }}>
              Each doctor will be randomly assigned to an available time slot between 9:00 AM and 5:00 PM (1-hour appointments). Core doctors are prioritized if the daily target is exceeded.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setAutoAssignModal(null)} disabled={autoAssigning}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            style={{ background: "#2E6E5E", borderColor: "#2E6E5E" }}
            onClick={async () => {
              if (!autoAssignModal) return;
              setAutoAssigning(true);
              try {
                const result = await autoAllocateDoctors(
                  autoAssignModal.docIds,
                  selectedStaffId,
                  cap
                );
                
                setAutoAssignModal(null);
                setSelectedDoctorIds(new Set());
                
                if (result.allocated > 0) {
                  Swal.fire(
                    "Auto-assigned",
                    `✓ ${result.cores} Core + ${result.secondary} Secondary doctors allocated to the week.\n\nClick "Save schedule" to persist to database.`,
                    "success"
                  );
                } else {
                  Swal.fire(
                    "No slots available",
                    "Couldn't find available time slots for any of the selected doctors. Try increasing the daily target or selecting fewer doctors.",
                    "warning"
                  );
                }
              } catch (err) {
                console.error("autoAllocateDoctors error:", err);
                Swal.fire("Error", "Failed to auto-assign doctors: " + (err.message || "Unknown error"), "error");
              } finally {
                setAutoAssigning(false);
              }
            }}
            disabled={autoAssigning}
          >
            {autoAssigning ? "Assigning…" : "Confirm & assign"}
          </Button>
        </Modal.Footer>
      </Modal>

      <TimeModal
        modalState={timeModal}
        saving={timeSaving}
        onClose={() => setTimeModal(null)}
        onSave={saveTimeModal}
        onClear={clearTime}
        onDelete={deleteAppointment}
      />
    </div>
  );
}

function TimeModal({ modalState, saving, onClose, onSave, onClear, onDelete }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (modalState) {
      setFrom(modalState.item.timeFrom || "");
      setTo(modalState.item.timeTo || "");
    }
  }, [modalState]);

  if (!modalState) return null;

  const isSaved = !!modalState.item.saved;

  return (
    <Modal show onHide={saving ? undefined : onClose} centered size="sm" backdrop={saving ? "static" : true}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title style={{ fontSize: 16 }}>{modalState.doc?.doctor_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: 12, color: "#5B6E70", marginBottom: 12 }}>
          {DAYS[modalState.day]}
          {isSaved && (
            <span style={{ marginLeft: 8, color: "#2E6E5E", fontWeight: 600 }}>
              · Saved — editing will update it immediately
            </span>
          )}
        </div>
        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12 }}>From</Form.Label>
          <Form.Control type="time" value={from} disabled={saving} onChange={(e) => setFrom(e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label style={{ fontSize: 12 }}>To</Form.Label>
          <Form.Control type="time" value={to} disabled={saving} onChange={(e) => setTo(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {isSaved && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={onDelete}
            disabled={saving}
          >
            {saving ? "Deleting…" : "Delete"}
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          style={{ background: "#2E6E5E", borderColor: "#2E6E5E" }}
          onClick={() => onSave(from, to)}
          disabled={saving}
        >
          {saving ? "Saving…" : isSaved ? "Update" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DoctorAssignment;
